import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Pagination from '../components/Pagination';
import ScrollToTop from '../components/ScrollToTop';
import { AppSidebar } from '../components/AppSidebar';
import { SidebarProvider, SidebarTrigger } from '../components/ui/sidebar';
import { API_BASE_URL } from '../config';
import ImageModal from '../components/ImageModal';
import VisitModal from '../components/VisitModal';
import { useVisit } from '../hooks/useVisit';
import { groupVisit, readingOrder } from '../utils/visitGroups';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import HeatmapModal from '../components/HeatmapModal';
import isEqual from 'lodash/isEqual';
import { GallerySelectionOverlayBar } from '../components/GallerySelectionOverlayBar';
import { useTranslation } from 'react-i18next';
import GridSizeToggle from '../components/GridSizeToggle';
import GalleryPictureCard from '../components/GalleryPictureCard';
import ExportPanel, { EXPORT_LIMITS } from '../components/ExportPanel';
import { useProjectJail } from '../hooks/useProjectJail';
import IntelligentSearchPanel from '../components/IntelligentSearchPanel';
import ViewToggles from '../components/ViewToggles';
import { createParams, splitProductFilter, toFacetParams } from '../utils/galleryParams';
import { can } from '../utils/can.js';

// `product` is a filter without a sidebar control: a code searched as « contains », not a value list.
const NON_DIMENSIONAL_FILTERS = ['createdat', 'favorite', 'deleted', 'product', 'product_item', 'product_ai', 'latest_visit'];

const LoadingIndicator = () => (
  <div className="max-w-7xl mx-auto p-2 sm:p-4">
    <div className="animate-pulse">
      {/* Header skeleton */}
      <div className="mb-4">
        <div className="h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
        <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>

      {/* Filters skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 mb-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="mb-1">
            <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-1"></div>
            <div className="h-[30px] bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        ))}
      </div>

      {/* Pictures grid skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
            <div className="h-48 bg-gray-200 dark:bg-gray-700"></div>
            <div className="p-4">
              <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
              <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded mb-1"></div>
              <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const getDatePart = (value) => String(value || '').slice(0, 10);

function Gallery({
  fetchWithAuth,
  currentUser,
  viewingAsClient = false,
  trackEvent,
  currentAppliedSearch,
  setCurrentAppliedSearch
}) {
  const { t } = useTranslation();
  const isAdmin = !!currentUser?.isadmin;
  const canFilterAi = can(currentUser, 'ai.filter');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pictureId: sharedPictureId, visitPictureId } = useParams();
  const [showHeatmap, setShowHeatmap] = useState(false);

  // Store initial obfuscate param in a ref to persist it
  const obfuscateRef = useRef(searchParams.has('obfuscate'));
  const obfuscate = obfuscateRef.current;

  const {
    isProjectJailActive,
    jailedProjectValues,
    jailedProjectOptions,
    jailedProjectLabelsLoading,
    sanitizeSelection: sanitizeJailedProjectSelection,
    getResetProjectsFilter,
    applyToSearchParams: applyJailToSearchParams,
  } = useProjectJail({ searchParams, fetchWithAuth });

  const allowedColumns = useMemo(() => {
    let nextAllowedColumns = [
      'client',
      'source',
      'project',
      'campaign',
      'translatedquestion',
      'item',
      'username',
      'sector',
      'shopsign',
      'shopname',
      // 'shopzipcode',
      'visittype',
    ];

    const adminColumns = ['username'];

    if (!currentUser?.isadmin)
      nextAllowedColumns = nextAllowedColumns.filter(column => !adminColumns.includes(column));

    if (!currentUser?.multiclient)
      nextAllowedColumns = nextAllowedColumns.filter(column => column !== 'client');

    return nextAllowedColumns;
  }, [currentUser?.isadmin, currentUser?.multiclient]);

  // 2. Then declare functions that use those constants
  const getDefaultDateRange = (currentUser) => {
    const end = new Date();
    const start = new Date();

    if (currentUser?.issuperadmin) {
      start.setDate(end.getDate() - 90);
    } else {
      start.setFullYear(end.getFullYear() - 1);
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  };

  const getDefaultFilters = (currentUser) => {
    return {
      createdat: getDefaultDateRange(currentUser)
    };
  };

  const getInitialFilters = () => {
    const urlFilters = {};

    // Handle date filter
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');

    if (startDate && endDate) {
      urlFilters.createdat = { startDate, endDate };
    } else {
      urlFilters.createdat = getDefaultDateRange(currentUser);
    }

    // Handle favorite filter
    const favorite = searchParams.get('favorite');
    if (favorite === 'true') {
      urlFilters.favorite = true;
    }

    if (searchParams.get('latest_visit') === 'true') {
      urlFilters.latest_visit = true;
    }

    // Handle deleted filter for admins
    const deleted = searchParams.get('deleted');
    if (deleted === 'true') {
      urlFilters.deleted = true;
    }

    // Handle other filters (supports both single and multi-select via key or key[])
    allowedColumns.forEach(column => {
      // Prefer array form if present: e.g. campaign[]
      const arrayValues = searchParams.getAll(`${column}[]`);
      if (arrayValues && arrayValues.length > 0) {
        urlFilters[column] = arrayValues.map(v => ({ value: v, label: v }));
        return;
      }

      // Fallback to single value form: e.g. campaign=...
      const singleValue = searchParams.get(column);
      if (singleValue) {
        urlFilters[column] = [{ value: singleValue, label: singleValue }];
      }
    });

    ['product', 'product_item', 'product_ai'].forEach((name) => {
      const codes = searchParams.getAll(`${name}[]`);
      if (codes.length > 0) urlFilters[name] = codes.map(v => ({ value: v, label: v }));
    });

    return splitProductFilter(urlFilters, { isAdmin: canFilterAi });
  };

  // 3. Then declare state variables
  const [pictures, setPictures] = useState([]);
  const [dimensionCounts, setDimensionCounts] = useState({});
  const [dimensionOptions, setDimensionOptions] = useState({});
  const [dimensionCountsLoading, setDimensionCountsLoading] = useState({});
  const [dimensionValuesLoading, setDimensionValuesLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [visitMode, setVisitMode] = useState(false);
  const visitOriginRef = useRef(null);
  const visit = useVisit({ pictureId: visitPictureId, fetchWithAuth, asClient: viewingAsClient });
  const { updatePicture: updateVisitPicture, removePicture: removeVisitPicture, contains: visitContains } = visit;
  const visitSequence = useMemo(() => readingOrder(groupVisit(visit.pictures)), [visit.pictures]);
  const navigationPictures = visitMode ? visitSequence : pictures;
  const [filters, setFilters] = useState(getInitialFilters());
  const [paginationInfo, setPaginationInfo] = useState({
    total: 0,
    isApproximated: false,
    page: 1,
    limit: 100,
    totalPages: 0
  });
  const [latestVisitRefused, setLatestVisitRefused] = useState(null);
  const [picturesFailure, setPicturesFailure] = useState(null);
  const [selectedPictures, setSelectedPictures] = useState([]);
  const [selectedPictureCache, setSelectedPictureCache] = useState({});
  const [showOptimizedCountInfo, setShowOptimizedCountInfo] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [gridSize, setGridSize] = useState(() => {
    try {
      return localStorage.getItem('gallery:gridSize') || 'medium';
    } catch (e) {
      return 'medium';
    }
  });
  const [exportPanel, setExportPanel] = useState(null);
  const openExport = useCallback((options = {}) => setExportPanel({ scope: 'search', format: null, searchText: null, ...options }), []);
  const [dateBoundsLoading, setDateBoundsLoading] = useState(false);
  const [dateBoundsError, setDateBoundsError] = useState('');
  const dimensionCountsRunIdRef = useRef({});
  const dimensionValuesRunIdRef = useRef({});
  const loadedDimensionParamsRef = useRef({});
  const loadedDimensionCountParamsRef = useRef({});
  const dimensionSearchTimersRef = useRef({});
  const loadedSharedPictureIdRef = useRef(null);
  const picturesRunIdRef = useRef(0);
  const picturesAbortControllerRef = useRef(null);

  // Entering the client preview drops the filters a client could never have set. Leaving them on
  // would apply a filter whose control has just disappeared from the sidebar — active, invisible,
  // and impossible to undo without leaving the preview.
  useEffect(() => {
    if (!viewingAsClient) return;
    setFilters((current) => {
      const kept = Object.fromEntries(Object.entries(current)
        .filter(([key]) => NON_DIMENSIONAL_FILTERS.includes(key) || allowedColumns.includes(key)));
      return Object.keys(kept).length === Object.keys(current).length ? current : kept;
    });
  }, [viewingAsClient, allowedColumns]);

  const buildParams = useCallback(() => createParams(
    filters,
    { jailedProjectValues: isProjectJailActive ? jailedProjectValues : [], viewingAsClient }
  ), [filters, isProjectJailActive, jailedProjectValues, viewingAsClient]);
  const buildFacetParams = useCallback(() => toFacetParams(buildParams()), [buildParams]);
  const facetParamsKey = useMemo(() => buildFacetParams().toString(), [buildFacetParams]);

  const fetchPictures = async () => {
    console.log('>>> fetchPictures', filters);
    const runId = picturesRunIdRef.current + 1;
    picturesRunIdRef.current = runId;
    picturesAbortControllerRef.current?.abort();
    const controller = new AbortController();
    picturesAbortControllerRef.current = controller;
    let retrying = false;

    try {
      if (!initialLoading)
        setContentLoading(true);

      const params = buildParams();
      params.append('page', paginationInfo.page);
      params.append('limit', paginationInfo.limit);

      const response = await fetchWithAuth(`${API_BASE_URL}/api/pictures?${params}`, { signal: controller.signal });
      const data = await response.json();
      if (response.status === 422 && data.code === 'latest_visit_too_broad') {
        if (picturesRunIdRef.current !== runId) return;
        retrying = true;
        setLatestVisitRefused(data.limit);
        setFilters(({ latest_visit: dropped, ...rest }) => rest);
        return;
      }
      if (response.status === 503 && data.code === 'query_timeout') {
        if (picturesRunIdRef.current !== runId) return;
        setPicturesFailure('query_timeout');
        setPictures([]);
        return;
      }
      if (!response.ok)
        throw new Error(data.error || 'Unable to load pictures');
      if (picturesRunIdRef.current !== runId)
        return;

      setPicturesFailure(null);
      const nextPictures = data.pictures || [];
      setPictures(nextPictures);
      setSelectedPictureCache(prev => ({
        ...prev,
        ...Object.fromEntries(nextPictures.map(picture => [picture.id, picture]))
      }));
      setPaginationInfo(prev => ({
        ...prev,
        ...data.pagination
      }));
      if (params.get('latest_visit') === 'true') setLatestVisitRefused(null);

    } catch (error) {
      if (error.name !== 'AbortError' && picturesRunIdRef.current === runId) {
        console.error('Error fetching pictures:', error);
        setPicturesFailure('error');
      }
    } finally {
      if (picturesRunIdRef.current === runId && !retrying) {
        setContentLoading(false);
        setInitialLoading(false);
        setLoading(false);
      }
    }
  };

  // Référence pour éviter de mettre à jour l'URL lors du premier rendu
  const skipFirstRender = useRef(true);

  useEffect(() => {
    try {
      localStorage.setItem('gallery:gridSize', gridSize);
    } catch (e) {
      // ignore
    }
  }, [gridSize]);

  const formatDimensionOptions = useCallback((column, values) => (
    column === 'project' || column === 'shopname'
      ? values
      : values.map(value => ({ value, label: value }))
  ), []);

  const ensureDimensionCountLoaded = useCallback(async (column, params) => {
    if (column === 'project' && isProjectJailActive) {
      setDimensionCounts(prev => ({ ...prev, project: jailedProjectValues.length }));
      return;
    }

    if (loadedDimensionCountParamsRef.current[column] === params)
      return;

    const runId = (dimensionCountsRunIdRef.current[column] || 0) + 1;
    dimensionCountsRunIdRef.current[column] = runId;
    loadedDimensionCountParamsRef.current[column] = params;
    setDimensionCountsLoading(prev => ({ ...prev, [column]: true }));
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/columns/${column}/count?${params}`);
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || `Unable to load ${column} count`);

      if (dimensionCountsRunIdRef.current[column] !== runId)
        return;

      setDimensionCounts(prev => ({ ...prev, [column]: data.count || 0 }));
    } catch (error) {
      if (dimensionCountsRunIdRef.current[column] === runId)
        delete loadedDimensionCountParamsRef.current[column];
      console.error(`Error fetching dimension count "${column}":`, error);
    } finally {
      if (dimensionCountsRunIdRef.current[column] !== runId)
        return;
      setDimensionCountsLoading(prev => ({ ...prev, [column]: false }));
    }
  }, [fetchWithAuth, isProjectJailActive, jailedProjectValues]);

  const ensureDimensionLoaded = useCallback(async (column, search = '') => {
    if (column === 'project' && isProjectJailActive)
      return;

    const paramsObject = buildFacetParams();
    const countParams = paramsObject.toString();
    ensureDimensionCountLoaded(column, countParams);
    const normalizedSearch = `${search || ''}`.normalize('NFKC').trim().slice(0, 100);
    if (normalizedSearch) paramsObject.set('q', normalizedSearch);
    paramsObject.set('limit', '100');
    const params = paramsObject.toString();

    if (loadedDimensionParamsRef.current[column] === params)
      return;

    const runId = (dimensionValuesRunIdRef.current[column] || 0) + 1;
    dimensionValuesRunIdRef.current[column] = runId;
    loadedDimensionParamsRef.current[column] = params;

    setDimensionValuesLoading(prev => ({ ...prev, [column]: true }));

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/columns/${column}?${params}`);
      const values = await response.json();

      if (!response.ok || !Array.isArray(values))
        throw new Error(values?.error || `Unable to load ${column} values`);

      if (dimensionValuesRunIdRef.current[column] !== runId)
        return;

      setDimensionOptions(prev => ({
        ...prev,
        [column]: formatDimensionOptions(column, values)
      }));
    } catch (error) {
      if (dimensionValuesRunIdRef.current[column] === runId)
        delete loadedDimensionParamsRef.current[column];
      console.error(`Error fetching dimension values "${column}":`, error);
    } finally {
      if (dimensionValuesRunIdRef.current[column] !== runId)
        return;

      setDimensionValuesLoading(prev => ({ ...prev, [column]: false }));
    }
  }, [fetchWithAuth, buildFacetParams, formatDimensionOptions, isProjectJailActive, ensureDimensionCountLoaded]);

  const searchDimension = useCallback((column, search) => {
    window.clearTimeout(dimensionSearchTimersRef.current[column]);
    dimensionSearchTimersRef.current[column] = window.setTimeout(() => {
      ensureDimensionLoaded(column, search);
    }, 250);
  }, [ensureDimensionLoaded]);

  // 7.Update URL when filters change
  useEffect(() => {
    // Skip the first render to avoid overriding URL params
    if (skipFirstRender.current) {
      skipFirstRender.current = false;
      return;
    }

    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'createdat' && value) {
        params.set('startDate', value.startDate);
        params.set('endDate', value.endDate);
      } else if (key === 'favorite' || key === 'latest_visit') {
        if (value === true) {
          params.set(key, 'true');
        }
      }
      // NEW: handle "deleted" filter parameter
      else if (key === 'deleted') {
        if (value === true) {
          params.set('deleted', 'true');
        }
      } else if (Array.isArray(value)) {
        // Handle array values
        value.forEach(v => {
          params.append(`${key}[]`, v.value);
        });
      } else if (value?.value) {
        params.set(key, value.value);
      }
    });

    // Keep obfuscate param if it exists
    if (obfuscate) {
      params.set('obfuscate', 'true');
    }

    applyJailToSearchParams(params);

    params.set('page', paginationInfo.page.toString());
    setSearchParams(params, { replace: true });
  }, [filters, paginationInfo.page, setSearchParams, obfuscate, applyJailToSearchParams]);

  // Fetch facet counts for filters once the first pictures payload is loaded
  useEffect(() => {
    if (initialLoading)
      return;

    Object.keys(dimensionValuesRunIdRef.current).forEach(column => {
      dimensionValuesRunIdRef.current[column] += 1;
    });
    Object.keys(dimensionCountsRunIdRef.current).forEach(column => {
      dimensionCountsRunIdRef.current[column] += 1;
    });
    Object.values(dimensionSearchTimersRef.current).forEach(timer => window.clearTimeout(timer));
    dimensionSearchTimersRef.current = {};
    loadedDimensionParamsRef.current = {};
    loadedDimensionCountParamsRef.current = {};
    setDimensionOptions({});
    setDimensionValuesLoading({});
    setDimensionCounts({});
    setDimensionCountsLoading({});
  }, [facetParamsKey, initialLoading]);

  useEffect(() => () => {
    Object.values(dimensionSearchTimersRef.current).forEach(timer => window.clearTimeout(timer));
    picturesRunIdRef.current += 1;
    picturesAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    if (initialLoading)
      return;

    Object.keys(filters)
      .filter(column => allowedColumns.includes(column) && !NON_DIMENSIONAL_FILTERS.includes(column))
      .filter(column => column !== 'project' || !isProjectJailActive)
      .forEach(column => {
        ensureDimensionLoaded(column);
      });
  }, [filters, allowedColumns, initialLoading, ensureDimensionLoaded, isProjectJailActive]);

  // Update fetchPictures effect
  useEffect(() => {
    fetchPictures();
  }, [JSON.stringify(filters), paginationInfo.page, paginationInfo.limit]);

  useEffect(() => {
    if (!sharedPictureId || loadedSharedPictureIdRef.current === sharedPictureId)
      return;

    loadedSharedPictureIdRef.current = sharedPictureId;

    const fetchSharedPicture = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/pictures/${sharedPictureId}`);

        if (!response.ok)
          throw new Error('Picture not found');

        const picture = await response.json();
        setSelectedPictureCache(prev => ({ ...prev, [picture.id]: picture }));
        setSelectedImage(picture);
      } catch (error) {
        console.error('Error fetching shared picture:', error);
        loadedSharedPictureIdRef.current = null;
      }
    };

    fetchSharedPicture();
  }, [fetchWithAuth, sharedPictureId]);

  // Update handleFilterChange to maintain URL sync
  const handleFilterChange = (key, value, isBulk = false) => {
    const newFilters = { ...filters };

    if (isBulk) {
      Object.entries(value).forEach(([key, value]) => {
        if (key === 'project' && isProjectJailActive) {
          const sanitizedValue = sanitizeJailedProjectSelection(value);

          newFilters[key] = sanitizedValue.length === 0
            ? getResetProjectsFilter() || []
            : sanitizedValue;

          return;
        }

        if (value === null) {
          delete newFilters[key];
        } else {
          newFilters[key] = value;
        }
      });

      trackEvent('saved_search', {
        props: {
          name: key,
          user: currentUser?.username,
          client: currentUser?.client,
        }
      });

      setFilters(newFilters);

      return;
    }

    trackEvent('filter', {
      props: {
        key,
        user: currentUser?.username,
        client: currentUser?.client,
      }
    });

    if (key === 'project' && isProjectJailActive) {
      const sanitizedValue = sanitizeJailedProjectSelection(value);
      newFilters[key] = sanitizedValue.length === 0
        ? getResetProjectsFilter() || []
        : sanitizedValue;
    } else if (value === null) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }

    setFilters(newFilters);
  };

  // Both sides at once: two calls to handleFilterChange would each start from the same filters and
  // the second would undo the first.
  const handleProductChange = useCallback(({ item = [], ai = [] }) => {
    setFilters((current) => {
      const { product, product_item: previousItem, product_ai: previousAi, ...rest } = current;
      return {
        ...rest,
        ...(item.length ? { product_item: item } : {}),
        ...(ai.length && canFilterAi ? { product_ai: ai } : {})
      };
    });
    setPaginationInfo(previous => ({ ...previous, page: 1 }));
  }, [canFilterAi]);

  const currentSearch = useCallback(() => {
    const search = searchParams.toString();
    return search ? `?${search}` : '';
  }, [searchParams]);

  const handleOpenVisit = useCallback((picture) => {
    visitOriginRef.current = picture;
    setVisitMode(false);
    setSelectedImage(null);
    navigate(`/gallery/visit/${picture.id}${currentSearch()}`);
  }, [currentSearch, navigate]);

  const handleOpenVisitPicture = useCallback((picture) => {
    setVisitMode(true);
    setSelectedImage(picture);
  }, []);

  const handleBackToVisit = useCallback(() => {
    const viewed = selectedImage;
    setVisitMode(false);
    setSelectedImage(null);
    if (viewed && String(viewed.id) !== visitPictureId && visitContains(viewed.id))
      navigate(`/gallery/visit/${viewed.id}${currentSearch()}`, { replace: true });
  }, [currentSearch, navigate, selectedImage, visitContains, visitPictureId]);

  const handleCloseVisit = useCallback(() => {
    const origin = visitOriginRef.current;
    visitOriginRef.current = null;
    setVisitMode(false);
    navigate(`/gallery${currentSearch()}`);
    if (origin) setSelectedImage(origin);
  }, [currentSearch, navigate]);

  // Handle ESC key press
  const handleEscKey = useCallback((event) => {
    if (event.key === 'Escape') {
      if (visitMode) {
        handleBackToVisit();
        return;
      }
      setSelectedImage(null);
      if (sharedPictureId) {
        navigate('/gallery', { replace: true });
      }
    }
  }, [handleBackToVisit, navigate, sharedPictureId, visitMode]);

  // Add/remove event listener for ESC key
  useEffect(() => {
    if (selectedImage) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [selectedImage, handleEscKey]);

  // The visit is the layer under the photo: Escape reaches it only once the photo is closed.
  useEffect(() => {
    if (!visitPictureId || selectedImage) return undefined;

    const closeOnEscape = (event) => { if (event.key === 'Escape') handleCloseVisit(); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [handleCloseVisit, selectedImage, visitPictureId]);

  // Add this new function inside the Gallery component
  const handlePageChange = (newPage) => {
    setPaginationInfo(prev => ({
      ...prev,
      page: newPage
    }));

    // Scroll to top when changing page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleSelectPicture = (picture) => {
    const pictureId = typeof picture === 'object' ? picture.id : picture;

    if (typeof picture === 'object') {
      setSelectedPictureCache(prev => ({ ...prev, [picture.id]: picture }));
    }

    setSelectedPictures(prev => {
      if (prev.includes(pictureId)) {
        return prev.filter(id => id !== pictureId);
      }
      return [...prev, pictureId];
    });
  };

  const handleSelectAll = () => {
    if (selectedPictures.length === pictures.length) {
      setSelectedPictures([]);
    } else {
      setSelectedPictureCache(prev => ({
        ...prev,
        ...Object.fromEntries(pictures.map(picture => [picture.id, picture]))
      }));
      setSelectedPictures(pictures.map(p => p.id));
    }
  };

  const handleClearSelection = () => {
    setSelectedPictures([]);
  };

  const handleRemoveSelectedPicture = (pictureId) => {
    setSelectedPictures(prev => prev.filter(id => id !== pictureId));
  };

  // Adding does not open the cart: from the viewer one adds several photos in a row, and a panel
  // opening each time interrupts exactly that. The button's own state is the confirmation.
  const handleAddImageToExportCart = (picture) => {
    setSelectedPictureCache(prev => ({ ...prev, [picture.id]: picture }));
    setSelectedPictures(prev => prev.includes(picture.id) ? prev : [...prev, picture.id]);
  };

  const trackExport = ({ format, scope, count }) => trackEvent('export', {
    props: {
      count,
      format,
      scope,
      user: currentUser?.username,
      client: currentUser?.client,
    }
  });

  // Update handleToggleFavorite to modify the picture's favorite status
  const handleToggleFavorite = useCallback(async (pictureId, favorite) => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/pictures/${pictureId}/favorite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ favorite })
        }
      );

      if (response.ok) {
        // Update the favorite status in both pictures and selectedImage
        setPictures(prev => prev.map(pic =>
          pic.id === pictureId ? { ...pic, favorite } : pic
        ));
        updateVisitPicture(pictureId, { favorite });

        if (selectedImage?.id === pictureId) {
          setSelectedImage(prev => ({ ...prev, favorite }));
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  }, [fetchWithAuth, selectedImage, updateVisitPicture]);

  // Handle image update after editing
  const handleImageUpdate = useCallback((updatedImage) => {
    // Update the image in the pictures array
    setPictures(prev => prev.map(pic =>
      pic.id === updatedImage.id ? updatedImage : pic
    ));
    updateVisitPicture(updatedImage.id, updatedImage);

    // Update selectedImage if it's the same image
    if (selectedImage?.id === updatedImage.id) {
      setSelectedImage(updatedImage);
    }
  }, [selectedImage, updateVisitPicture]);

  const handleDeleteSelected = async () => {
    // Show confirmation dialog before deleting
    if (!window.confirm(t('Are you sure you want to delete these pictures? This action cannot be undone.'))) {
      return; // User cancelled the operation
    }

    try {
      await fetchWithAuth(
        `${API_BASE_URL}/api/pictures/delete`, { method: 'POST', body: JSON.stringify({ ids: selectedPictures }) }
      );
      // Clear selection after successful deletion
      setSelectedPictures([]);
      fetchPictures();
    } catch (error) {
      console.error('Error deleting pictures:', error);
    }
  };

  // Add a content loading indicator component
  const ContentLoadingIndicator = () => (
    <div className="absolute inset-0 bg-white dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-10">
      <div className="animate-pulse flex flex-col items-center">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <span className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('Loading...')}</span>
      </div>
    </div>
  );

  // Add reset filters function
  /**
   * The filters a reset lands on — and therefore what « no filter » means when counting the active
   * ones. Both must agree, or a reset leaves the gallery believing a filter is still set.
   */
  const getResetFilters = () => {
    const resetFilters = { createdat: getDefaultDateRange(currentUser) };
    const resetProjects = getResetProjectsFilter();
    if (resetProjects) resetFilters.project = resetProjects;
    return resetFilters;
  };

  const handleResetFilters = useCallback(() => {
    const nextFilters = getResetFilters();
    setFilters(nextFilters);

    // Reset pagination to page 1
    setPaginationInfo(prev => ({
      ...prev,
      page: 1
    }));

    // Clear URL params except for page=1
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('startDate', nextFilters.createdat.startDate);
    params.set('endDate', nextFilters.createdat.endDate);

    applyJailToSearchParams(params);

    setSearchParams(params, { replace: true });
    setCurrentAppliedSearch(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams, getResetProjectsFilter, applyJailToSearchParams, setCurrentAppliedSearch, currentUser?.issuperadmin]);

  const applyIntelligentFilters = useCallback((plannedFilters) => {
    const nextFilters = splitProductFilter({ ...plannedFilters }, { isAdmin: canFilterAi });
    const resetProjects = getResetProjectsFilter();
    if (resetProjects) {
      const requestedProjects = sanitizeJailedProjectSelection(nextFilters.project || []);
      nextFilters.project = requestedProjects.length > 0 ? requestedProjects : resetProjects;
    }
    setFilters(nextFilters);
    setPaginationInfo(previous => ({ ...previous, page: 1 }));
    setCurrentAppliedSearch(null);
  }, [getResetProjectsFilter, sanitizeJailedProjectSelection, setCurrentAppliedSearch, canFilterAi]);

  const handleAdjustDateRangeToResults = useCallback(async () => {
    setDateBoundsLoading(true);
    setDateBoundsError('');

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/pictures/date-bounds?${buildParams()}`);
      if (!response.ok)
        throw new Error('Unable to fetch matching date range');

      const data = await response.json();

      if (!data.minDate || !data.maxDate || data.count === 0) {
        setDateBoundsError(t('No image matches the selected filters, regardless of date range.'));
        return;
      }

      setFilters(prev => ({
        ...prev,
        createdat: {
          startDate: getDatePart(data.minDate),
          endDate: getDatePart(data.maxDate)
        }
      }));

      setPaginationInfo(prev => ({
        ...prev,
        page: 1
      }));
    } catch (error) {
      console.error('Error adjusting date range:', error);
      setDateBoundsError(t('Unable to adjust date range automatically.'));
    } finally {
      setDateBoundsLoading(false);
    }
  }, [buildParams, fetchWithAuth, t]);

  const getCurrentImageIndex = () => {
    return navigationPictures.findIndex(p => p.id === selectedImage.id);
  };

  const handlePreviousImage = () => {
    const currentIndex = getCurrentImageIndex();
    if (currentIndex > 0) {
      setSelectedImage(navigationPictures[currentIndex - 1]);
    }
  };

  const handleNextImage = () => {
    const currentIndex = getCurrentImageIndex();
    if (currentIndex < navigationPictures.length - 1) {
      setSelectedImage(navigationPictures[currentIndex + 1]);
    }
  };

  const handleDeletePicture = async (pictureId) => {
    try {
      await fetchWithAuth(
        `${API_BASE_URL}/api/pictures/${pictureId}/delete`, { method: 'POST', noLogOut: true }
      );
      // Remove the picture from the local state
      setPictures(pictures.filter(p => p.id !== pictureId));
      removeVisitPicture(pictureId);
    } catch (error) {
      console.error('Error deleting picture:', error);
      throw error;
    }
  };

  // Handle admin only toggle
  const handleToggleAdminOnly = useCallback(async (pictureId, adminOnly) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/pictures/${pictureId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          picture: { adminOnly }
        })
      });

      if (!response.ok) throw new Error('Erreur lors du changement adminOnly');

      const updatedPicture = await response.json();

      // Update the admin_only status in both pictures and selectedImage
      setPictures(prev => prev.map(pic =>
        pic.id === pictureId ? { ...pic, admin_only: adminOnly } : pic
      ));
      updateVisitPicture(pictureId, { admin_only: adminOnly });

      if (selectedImage?.id === pictureId) {
        setSelectedImage(prev => ({ ...prev, admin_only: adminOnly }));
      }
    } catch (error) {
      console.error('Error toggling admin only:', error);
      throw error;
    }
  }, [fetchWithAuth, selectedImage, updateVisitPicture]);

  const activeFiltersCount = () => {
    const defaultFilters = getResetFilters();
    const sanitizedFilters = { ...filters };

    // remove null values or empty arrays from defaultFilters
    Object.keys(sanitizedFilters).forEach(key => {
      if (sanitizedFilters[key] === null || (Array.isArray(sanitizedFilters[key]) && sanitizedFilters[key].length === 0)) {
        delete sanitizedFilters[key];
      }
    });

    if (!sanitizedFilters.project) delete defaultFilters.project;

    if (isEqual(sanitizedFilters, defaultFilters))
      return 0;

    return Object.keys(sanitizedFilters).length;
  };

  const stickyHeaderRef = useRef(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);

  useEffect(() => {
    const headerEl = stickyHeaderRef.current;
    if (!headerEl)
      return undefined;

    const updateHeight = () => {
      setStickyHeaderHeight(headerEl.getBoundingClientRect().height || 0);
    };

    updateHeight();

    if (typeof ResizeObserver === 'undefined')
      return undefined;

    const ro = new ResizeObserver(() => updateHeight());
    ro.observe(headerEl);

    return () => ro.disconnect();
  }, []);

  if (loading)
    return <LoadingIndicator />;

  const activeFiltersCountValue = activeFiltersCount();
  // The whole search is offered for export only when it can actually be exported: a filter narrows
  // it, its total is exact, and it fits the Excel limit. Past that, the bar says why instead.
  const searchExportable = activeFiltersCountValue > 0 && !paginationInfo.isApproximated
    && paginationInfo.total > 0 && paginationInfo.total <= EXPORT_LIMITS.xlsx;
  const jailedFilterOptions = isProjectJailActive ? {
    project: jailedProjectOptions
  } : {};
  const hiddenColumns = isProjectJailActive && jailedProjectValues.length === 1 ? ['project'] : [];
  const galleryLoadingColumns = isProjectJailActive ? {
    ...dimensionValuesLoading,
    project: jailedProjectLabelsLoading
  } : dimensionValuesLoading;
  const selectedPictureItems = selectedPictures
    .map(id => selectedPictureCache[id] || pictures.find(picture => picture.id === id))
    .filter(Boolean);

  const handleCloseSelectedImage = () => {
    setSelectedImage(null);

    if (sharedPictureId) {
      navigate('/gallery', { replace: true });
    }
  };

  const getTotalCountInfo = (paginationInfo) => {
    if (paginationInfo?.isApproximated)
      return '~' + (paginationInfo?.total / 1000).toFixed(0) + 'k';

    return paginationInfo?.total.toLocaleString('fr-FR');
  }

  return (
    <SidebarProvider>
      <div className="flex flex-col min-h-screen w-full bg-gray-50 dark:bg-black">
        {/* Full Width Sticky Header - Local Toolbar */}
        <div ref={stickyHeaderRef} className="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 py-2 shadow-sm">
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                     <div className="flex items-center gap-2">
                        <SidebarTrigger />
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-gray-500 dark:text-gray-400">{getTotalCountInfo(paginationInfo)} {t('result', { count: paginationInfo.total })}</span>
                          {paginationInfo?.isApproximated === true && (
                            <div className="relative">
                              <button
                                className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors active:scale-95 md:group"
                                onClick={() => setShowOptimizedCountInfo(!showOptimizedCountInfo)}
                                onMouseEnter={() => window.innerWidth >= 768 && setShowOptimizedCountInfo(true)}
                                onMouseLeave={() => window.innerWidth >= 768 && setShowOptimizedCountInfo(false)}
                              >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                              </button>

                              {/* Tooltip (same for mobile and desktop) */}
                              {showOptimizedCountInfo && (
                                <>
                                  {/* Backdrop for mobile click-outside */}
                                  <div
                                    className="md:hidden fixed inset-0 z-40"
                                    onClick={() => setShowOptimizedCountInfo(false)}
                                  />
                                  <div className="absolute left-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3 text-xs z-50">
                                    <p className="font-medium text-gray-700 dark:text-gray-200 mb-1.5">{t('Optimized count')}</p>
                                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                                      {t('The total number of results and pages is estimated to optimize performance. Please apply at least one filter to get an exact count.')}
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <ViewToggles
                          filters={filters}
                          isAdmin={isAdmin}
                          t={t}
                          unavailable={{
                            latest_visit: paginationInfo?.latestVisitLimit && paginationInfo.total > paginationInfo.latestVisitLimit
                              ? t('Latest visit is available up to {{limit}} photos: narrow the search.', { limit: Number(paginationInfo.latestVisitLimit).toLocaleString() })
                              : null
                          }}
                          onToggle={(key, value) => {
                            handleFilterChange(key, value);
                            setPaginationInfo(previous => ({ ...previous, page: 1 }));
                          }}
                        />
                     </div>

                    <div className="flex items-center gap-2">
                        <GridSizeToggle value={gridSize} onChange={setGridSize} t={t} />
                        <Pagination
                          paginationInfo={paginationInfo}
                          onPageChange={handlePageChange}
                          totalFilters={Object.keys(filters).length}
                        />

                        {isAdmin && (
                          <button
                            onClick={() => setShowHeatmap(true)}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                            title="Heatmap"
                          >
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <rect x="2" y="2" width="20" height="20" rx="2" strokeWidth="2"/>
                              <path d="M7 16V8M12 16V4M17 16v-5" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                          </button>
                        )}

                        {can(currentUser, 'pictures.export') && (
                          <button
                            onClick={() => openExport({ scope: 'selection' })}
                            className="relative p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                            title={t('Export cart')}
                          >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l3-8H5.4M7 13L5.4 5M7 13l-2 6h14M9 19a1 1 0 11-2 0 1 1 0 012 0zm10 0a1 1 0 11-2 0 1 1 0 012 0z" />
                            </svg>
                            {selectedPictures.length > 0 && (
                              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1 text-[0.65rem] font-bold text-white ring-2 ring-white dark:ring-gray-900">
                                {selectedPictures.length}
                              </span>
                            )}
                          </button>
                        )}
                    </div>
                </div>

            </div>
        </div>

        <div className="flex flex-1 items-start">
            <AppSidebar
              className="top-[5.5rem]" // Offset for sticky header
              dimensions={dimensionOptions}
              dimensionCounts={dimensionCounts}
              loadingColumns={galleryLoadingColumns}
              loadingCounts={dimensionCountsLoading}
              onMenuOpen={ensureDimensionLoaded}
              onSearch={searchDimension}
              filters={filters}
              handleFilterChange={handleFilterChange}
              onProductChange={handleProductChange}
              handleResetFilters={handleResetFilters}
              allowedColumns={allowedColumns}
              fetchWithAuth={fetchWithAuth}
              defaultFilters={getDefaultFilters(currentUser)}
              paginationInfo={paginationInfo}
              activeFiltersCount={activeFiltersCountValue}
              canFilterAi={canFilterAi}
              currentAppliedSearch={currentAppliedSearch}
              setCurrentAppliedSearch={setCurrentAppliedSearch}
              obfuscate={obfuscate}
              jailedFilterOptions={jailedFilterOptions}
              hiddenColumns={hiddenColumns}
            />
            <main className="flex-1 min-w-0 transition-all duration-300">

                {/* Content */}
                <div className="p-4">
                  {latestVisitRefused && (
                    <div className="mb-3 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                      {t('Latest visit is available up to {{limit}} photos: narrow the search.', { limit: Number(latestVisitRefused).toLocaleString() })}
                      <button type="button" className="ml-auto text-amber-600 hover:text-amber-900" aria-label={t('Close')} onClick={() => setLatestVisitRefused(null)}>×</button>
                    </div>
                  )}

                  {can(currentUser, 'intelligent_search') && (
                    <IntelligentSearchPanel
                      fetchWithAuth={fetchWithAuth}
                      onApplyFilters={applyIntelligentFilters}
                      onReset={handleResetFilters}
                      resultCount={paginationInfo.total}
                      resultsLoading={contentLoading}
                      resultsFailure={picturesFailure}
                      defaultDateRange={getDefaultDateRange(currentUser)}
                      onExport={can(currentUser, 'export.excel') && searchExportable ? (searchText) => openExport({ format: 'xlsx', searchText }) : null}
                      canRate={can(currentUser, 'intelligent_search.rate')}
                      canFilterAi={canFilterAi}
                      asClient={viewingAsClient}
                    />
                  )}

                   {showHeatmap && (
                    <HeatmapModal
                      onClose={() => setShowHeatmap(false)}
                      filters={filters}
                      fetchWithAuth={fetchWithAuth}
                    />
                  )}

                  <ExportPanel
                    isOpen={!!exportPanel}
                    initialScope={exportPanel?.scope}
                    initialFormat={exportPanel?.format}
                    searchText={exportPanel?.searchText}
                    filters={filters}
                    buildQuery={buildParams}
                    searchTotal={paginationInfo.total}
                    searchExportable={searchExportable}
                    canExportPhotos={can(currentUser, 'pictures.export')}
                    selectedPictures={selectedPictureItems}
                    canDelete={can(currentUser, 'pictures.delete')}
                    fetchWithAuth={fetchWithAuth}
                    onClose={() => setExportPanel(null)}
                    onRemovePicture={handleRemoveSelectedPicture}
                    onClearSelection={handleClearSelection}
                    onDeleteSelected={handleDeleteSelected}
                    onExported={trackExport}
                    t={t}
                  />

                  {initialLoading ? (
                     <LoadingIndicator />
                   ) : (
                    <div className={contentLoading ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
                         {picturesFailure === 'query_timeout' ? (
                            <div className="flex flex-col items-center justify-center py-24 text-center">
                                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                                    {t('This search took too long')}
                                  </h3>
                                  <p className="text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
                                    {t('Its AI filters, product code or latest visit are too heavy to answer in time over this scope: narrow the period, the programmes or the conditions.')}
                                  </p>
                                  <button
                                    onClick={() => fetchPictures()}
                                    className="mt-6 px-5 py-3 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                                  >
                                    {t('Retry')}
                                  </button>
                                  <button
                                    onClick={handleResetFilters}
                                    className="mt-4 text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                  >
                                    {t('Or reset all filters')}
                                  </button>
                            </div>
                         ) : pictures.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-24 text-center">
                                 <svg
                                    className="w-24 h-24 text-gray-300 dark:text-gray-700 mb-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={1.5}
                                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                    />
                                  </svg>
                                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                                    {t('No results found')}
                                  </h3>
                                  <p className="text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
                                    {t('No image matches the selected date range. We can search for the date range that contains photos matching your current filters.')}
                                  </p>
                                  {dateBoundsError && (
                                    <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                                      {dateBoundsError}
                                    </p>
                                  )}
                                  <button
                                    onClick={handleAdjustDateRangeToResults}
                                    disabled={dateBoundsLoading}
                                    className="mt-6 px-5 py-3 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    {dateBoundsLoading ? t('Searching matching dates...') : t('Find the matching date range')}
                                  </button>
                                  <button
                                    onClick={handleResetFilters}
                                    className="mt-4 text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                  >
                                    {t('Or reset all filters')}
                                  </button>
                            </div>
                         ) : (
                            <div className="relative">
                              <GallerySelectionOverlayBar
                                activeFiltersCount={activeFiltersCountValue}
                                selectedCount={selectedPictures.length}
                                visibleCount={pictures.length}
                                stickyTop={stickyHeaderHeight}
                                onResetFilters={handleResetFilters}
                                onSelectAll={handleSelectAll}
                                total={paginationInfo.total}
                                onOpenExport={can(currentUser, 'export.excel') && searchExportable ? () => openExport() : null}
                                exportLimit={can(currentUser, 'export.excel') && !searchExportable ? EXPORT_LIMITS.xlsx : null}
                              />

                              <div
                                className={[
                                  "grid grid-cols-2 gap-4",
                                  // Desktop-only variants (mobile stays 2 columns)
                                  gridSize === 'small' ? "md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8" : "",
                                  gridSize === 'medium' ? "md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6" : "",
                                  gridSize === 'large' ? "md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4" : ""
                                ].filter(Boolean).join(' ')}
                              >
                                {pictures.map((picture) => (
                                  <GalleryPictureCard
                                    key={picture.id}
                                    picture={picture}
                                    currentUser={currentUser}
                                    isAdmin={isAdmin}
                                    isSelected={selectedPictures.includes(picture.id)}
                                    obfuscate={obfuscate}
                                    gridSize={gridSize}
                                    onOpen={() => setSelectedImage(picture)}
                                    onToggleSelection={() => handleSelectPicture(picture)}
                                    t={t}
                                  />
                                ))}
                              </div>
                            </div>
                         )}
                    </div>
                  )}

                  {visitPictureId && (
                    <VisitModal
                      visit={visit}
                      highlightId={visitPictureId}
                      isAdmin={isAdmin}
                      onOpenPicture={handleOpenVisitPicture}
                      onClose={handleCloseVisit}
                    />
                  )}

                  {selectedImage && (
                    <ImageModal
                      image={selectedImage}
                      onClose={visitMode ? handleBackToVisit : handleCloseSelectedImage}
                      onToggleFavorite={(pictureId) => handleToggleFavorite(pictureId, !selectedImage.favorite)}
                      onToggleAdminOnly={handleToggleAdminOnly}
                      onDelete={handleDeletePicture}
                      onPrevious={handlePreviousImage}
                      onNext={handleNextImage}
                      hasPrevious={getCurrentImageIndex() > 0}
                      hasNext={getCurrentImageIndex() < navigationPictures.length - 1}
                      curentUser={currentUser}
                      fetchWithAuth={fetchWithAuth}
                      onImageUpdate={handleImageUpdate}
                      onAddToExportCart={handleAddImageToExportCart}
                      isInExportCart={selectedPictures.includes(selectedImage.id)}
                      onOpenVisit={handleOpenVisit}
                      visitPosition={visitMode ? { index: getCurrentImageIndex() + 1, total: visitSequence.length } : null}
                      onBackToVisit={handleBackToVisit}
                    />
                  )}
                  <ScrollToTop />
                </div>
            </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

export default Gallery;
